// AndroidSqliteDB.js
// Durable, Android-native replacement for the IndexedDB layer.
// Uses @capacitor-community/sqlite so data survives what IndexedDB does not
// on Android WebView: LevelDB corruption, low-storage eviction, and
// WebView data clears triggered by the OS or an app/webview update.
//
// Design note: instead of ~100 separate object stores (one per DATA.ITEMS.*),
// everything lives in a single `kv_store` table keyed by (store_name, key).
// This mirrors the IndexedDB shape 1:1 (each "store" + keyPath:"key") without
// needing 100+ CREATE TABLE statements or a schema migration per new item type.
// The same table design will work unchanged for a future IOSSqliteDB.js.
//
// Public API is identical to IndexDB.js: addItems, updateItems, getItems,
// getUserDetails, deleteItems. Do not change call sites — only the import
// path changes (handled by the LocalDB.js router).

import DATA from './../DATA'
import { store } from './../../store'
import { CapacitorSQLite, SQLiteConnection } from '@capacitor-community/sqlite'

const NAME = 'ANDROID-SQLITE-DB'
const DB_NAME = 'kipcount_db'
const REQUEST_TIMER = 1

let sqliteConnection = null
let dbConn = null
let readyPromise = null

// ---------- connection + schema ----------

const initDB = () => {
  if (readyPromise) return readyPromise
  readyPromise = (async () => {
    sqliteConnection = new SQLiteConnection(CapacitorSQLite)

    // Guards against "connection already exists" errors on hot-reload / re-entry
    const consistency = await sqliteConnection.checkConnectionsConsistency()
    const isConn = (await sqliteConnection.isConnection(DB_NAME, false)).result

    if (consistency.result && isConn) {
      dbConn = await sqliteConnection.retrieveConnection(DB_NAME, false)
    } else {
      dbConn = await sqliteConnection.createConnection(DB_NAME, false, 'no-encryption', 1, false)
    }

    await dbConn.open()
    await dbConn.execute(`
      CREATE TABLE IF NOT EXISTS kv_store (
        store_name TEXT NOT NULL,
        key TEXT NOT NULL,
        data TEXT NOT NULL,
        updated_at INTEGER,
        PRIMARY KEY (store_name, key)
      );
    `)
    await dbConn.execute(`
      CREATE INDEX IF NOT EXISTS idx_kv_store_name ON kv_store(store_name);
    `)
    return dbConn
  })()
  return readyPromise
}

// ---------- low-level primitives ----------

const sqlPut = async (storeName, item) => {
  const db = await initDB()
  if (!item || item.key === undefined || item.key === null) {
    throw new Error('sqlPut: item.key is required')
  }
  await db.run(
    `INSERT OR REPLACE INTO kv_store (store_name, key, data, updated_at) VALUES (?,?,?,?)`,
    [storeName, String(item.key), JSON.stringify(item), Date.now()]
  )
  return item.key
}

const sqlGet = async (storeName, key) => {
  const db = await initDB()
  const res = await db.query(`SELECT data FROM kv_store WHERE store_name=? AND key=?`, [storeName, String(key)])
  const row = res.values && res.values[0]
  return row ? JSON.parse(row.data) : null
}

const sqlGetAll = async (storeName) => {
  const db = await initDB()
  const res = await db.query(`SELECT data FROM kv_store WHERE store_name=?`, [storeName])
  return (res.values || []).map(r => JSON.parse(r.data))
}

const sqlDeleteKey = async (storeName, key) => {
  const db = await initDB()
  await db.run(`DELETE FROM kv_store WHERE store_name=? AND key=?`, [storeName, String(key)])
}

const sqlClearStore = async (storeName) => {
  const db = await initDB()
  await db.run(`DELETE FROM kv_store WHERE store_name=?`, [storeName])
}

// ---------- addData / updateData (same contract as indexDB.js) ----------

const addData = (name, data, company_key, clear) => {
  return new Promise((resolve, reject) => {
    setTimeout(async () => {
      const response = { from: 'addData', page: NAME, name, data, clear }
      try {
        const storeName = name.toUpperCase()
        if (clear) {
          await sqlClearStore(storeName)
        }
        let value = null
        let data_after = null

        if (data) {
          if (Array.isArray(data)) {
            value = await Promise.all(data.map(item => sqlPut(storeName, item)))
          } else {
            value = await sqlPut(storeName, data)
          }
        }

        if (company_key) {
          data_after = await sqlGet(storeName, company_key)
        } else {
          data_after = await sqlGetAll(storeName)
        }

        resolve({
          response: 'success',
          action: 'add',
          success: true,
          data: value,
          data_after: data_after,
          clear: clear,
          name: name
        })
      } catch (error) {
        store.dispatch('ERROR', { ...response, error: { error, data, name } })
        reject({ ...response, error })
      }
    }, REQUEST_TIMER)
  })
}

// ---------- getData ----------

const getData = (name, key, update_store, company_key) => {
  return new Promise((resolve, reject) => {
    const response = { from: 'getData', page: NAME, name, key, update_store, company_key }
    setTimeout(async () => {
      try {
        const storeName = name.toUpperCase()
        let data = []
        if (company_key) {
          data = await sqlGet(storeName, company_key)
        } else if (key) {
          data = await sqlGet(storeName, key)
        } else {
          data = await sqlGetAll(storeName)
        }

        if (data && company_key) {
          data = data ? data.data : null
        }

        if (update_store) {
          await updateStore(name, data)
        }

        resolve({
          data: data,
          success: true,
          response: 'success',
          isArray: key ? false : true,
          update_store: update_store
        })
      } catch (error) {
        store.dispatch('ERROR', { ...response, error })
        reject({ ...response, error })
      }
    }, REQUEST_TIMER)
  })
}

// ---------- deleteStore / deleteStoreId ----------

const deleteStore = (name) => {
  return new Promise((resolve, reject) => {
    const response = { from: 'deleteStore', page: NAME, name }
    setTimeout(async () => {
      try {
        await sqlClearStore(name.toUpperCase())
        resolve({ response: 'success', success: true, name })
      } catch (error) {
        store.dispatch('ERROR', { ...response, error: { error, name } })
        reject({ ...response, error })
      }
    }, REQUEST_TIMER)
  })
}

const deleteStoreId = (name, key, items, company_key) => {
  return new Promise((resolve, reject) => {
    const response = { from: 'deleteStoreId', page: NAME, name, key }
    setTimeout(async () => {
      try {
        if (!key && !items) {
          reject({ ...response, error: 'No Key or Items' })
          return
        }
        const storeName = name.toUpperCase()

        if (key) {
          await sqlDeleteKey(storeName, key)
        } else if (items) {
          for (const item of items) {
            await sqlDeleteKey(storeName, item.key)
          }
        }

        let data_after = null
        if (company_key) {
          data_after = await sqlGet(storeName, company_key)
        } else {
          data_after = await sqlGetAll(storeName)
        }

        resolve({
          response: 'success',
          success: true,
          name,
          data_after,
          action: 'delete',
          key
        })
      } catch (error) {
        store.dispatch('ERROR', { ...response, error: { error, name, key } })
        reject({ ...response, error })
      }
    }, REQUEST_TIMER)
  })
}

// ---------- updateStore: pushes fetched data into Vuex ----------
// Same behavior as indexDB.js's big if/else chain, just data-driven instead
// of hand-written per field: every branch there does `state.<NAME> = data`
// where <NAME> is exactly name.toUpperCase() and the incoming `name` always
// matches an existing key on either store.state.users or store.state.items.
// Only ~9 keys live under `users`; everything else (including ERRORS) is
// `items`. Keep this list in sync with indexDB.js's updateStore — if you add
// a new DATA.ITEMS.* entry there under `users`, add its key here too.
const USER_STATE_KEYS = new Set([
  DATA.ITEMS.USERS.values.toUpperCase(),
  DATA.ITEMS.USER_DETAILS.values.toUpperCase(),
  DATA.ITEMS.USER_LIVES.values.toUpperCase(),
  DATA.ITEMS.USER_GROUPS.values.toUpperCase(),
  DATA.ITEMS.PERMISSIONS.values.toUpperCase(),
  DATA.ITEMS.BILLING_PLANS.values.toUpperCase(),
  DATA.ITEMS.BILLING_PLAN_FEATURES.values.toUpperCase(),
  DATA.ITEMS.CURRENT_BILLING_PLANS.values.toUpperCase(),
  DATA.ITEMS.SUBSCRIPTIONS.values.toUpperCase(),
  DATA.ITEMS.VISITERS.values.toUpperCase()
])

const updateStore = (name, data) => {
  return new Promise((resolve, reject) => {
    const response = { from: 'updateStore', page: NAME, name, data }
    try {
      if (!name) {
        reject({ ...response, error: 'payload required' })
        return
      }
      const user_state = store.state ? store.state.users : null
      const item_state = store.state ? store.state.items : null
      if (!user_state || !item_state) {
        reject({ ...response, error: { error: 'no state', data, name, store } })
        return
      }

      const key = name.toUpperCase()
      const target = USER_STATE_KEYS.has(key) ? user_state : item_state

      if (!(key in target)) {
        reject({ ...response, error: 'name not found' })
        return
      }

      target[key] = data
      resolve({ response: 'success', data, name })
    } catch (error) {
      store.dispatch('ERROR', { ...response, error })
      reject({ ...response, error })
    }
  })
}

// ---------- public API (identical shape to indexDB.js) ----------

const addItems = (payload) => {
  return new Promise((resolve, reject) => {
    const response = { from: 'addItems', page: NAME, payload }
    setTimeout(async () => {
      try {
        let { name, data, clear, company_key, update_from_db, update_locals } = payload || {}
        if (!name) {
          reject({ ...response, error: 'payload required' })
          return
        }
        if (company_key) {
          data = { key: company_key, data }
        }
        const res = await addData(name, data, company_key, clear)
        if (!update_from_db) {
          store.dispatch('update_from_db', payload)
        }
        if (update_locals) {
          store.dispatch('on_update_locals', { ...payload, ...res })
        }
        resolve({ ...res, success: true })
      } catch (error) {
        reject({ ...response, error })
      }
    }, REQUEST_TIMER)
  })
}

const updateItems = (payload) => {
  return new Promise((resolve, reject) => {
    const response = { from: 'updateItems', page: NAME, payload }
    setTimeout(async () => {
      try {
        let { name, data, clear, company_key, update_from_db, update_locals } = payload || {}
        if (!name) {
          reject({ ...response, error: 'payload required' })
          return
        }
        if (company_key) {
          data = { key: company_key, data }
        }

        if (data) {
          if (Array.isArray(data)) {
            const updated = []
            for (const item of data) {
              const currentRes = await getItems({ key: item.key, name, company_key })
              const current = currentRes && currentRes.data
              updated.push(current ? { ...current, ...item } : { ...item })
            }
            data = updated
          } else {
            const currentRes = await getItems({ key: data.key, name, company_key })
            const current = currentRes && currentRes.data
            if (current) data = { ...current, ...data }
          }
        }

        const res = await addData(name, data, company_key, clear)
        if (!update_from_db) {
          store.dispatch('update_from_db', payload)
        }
        if (update_locals) {
          store.dispatch('on_update_locals', { ...payload, ...res })
        }
        resolve({ ...res, success: true })
      } catch (error) {
        reject({ ...response, error })
      }
    }, REQUEST_TIMER)
  })
}

const getItems = (payload) => {
  return new Promise((resolve, reject) => {
    const response = { from: 'getItems', page: NAME, payload }
    setTimeout(async () => {
      try {
        const { name, key, update_store, company_key, update_from_db } = payload || {}
        if (!name) {
          reject({ ...response, error: 'payload required' })
          return
        }
        const res = await getData(name, key, update_store, company_key)
        if (!update_from_db) {
          store.dispatch('update_from_db', payload)
        }
        resolve({ ...res, success: true })
      } catch (error) {
        reject({ ...response, error })
      }
    }, REQUEST_TIMER)
  })
}

const getUserDetails = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const current = await getItems({ name: DATA.ITEMS.USER_LIVES.values, key: DATA.APP.USER_TIMELINE.current })
      const action_request = await getItems({ name: DATA.ITEMS.USER_LIVES.values, key: DATA.APP.USER_TIMELINE.action_request })
      const logged = await getItems({ name: DATA.ITEMS.USER_LIVES.values, key: DATA.APP.USER_TIMELINE.logged_out })

      const current_uid = current && current.data ? current.data.uid : null
      const action_request_uid = action_request && action_request.data ? action_request.data.uid : null
      const logged_uid = logged && logged.data ? logged.data.uid : null

      let user_details_data = null
      if (action_request_uid) {
        user_details_data = await getItems({ name: DATA.ITEMS.USER_DETAILS.values, key: action_request_uid })
      } else if (current_uid) {
        user_details_data = await getItems({ name: DATA.ITEMS.USER_DETAILS.values, key: current_uid })
      }
      const user_details = user_details_data ? user_details_data.data : null

      resolve({
        response: 'success',
        current_uid,
        action_request_uid,
        logged_uid,
        user_details
      })
    } catch (error) {
      store.dispatch('ERROR', { error, from: 'getUserDetails', page: NAME })
      reject({ error, from: 'getUserDetails', page: NAME, response: 'not success' })
    }
  })
}

const deleteItems = (payload) => {
  return new Promise((resolve, reject) => {
    const response = { from: 'deleteItems', page: NAME, payload }
    setTimeout(async () => {
      try {
        const { name, key, items, company_key, update_locals } = payload || {}
        if (!name) {
          reject({ ...response, error: 'payload required' })
          return
        }
        let res = null
        if (key || items) {
          res = await deleteStoreId(name, key, items, company_key)
        } else {
          res = await deleteStore(name, company_key)
        }
        if (update_locals) {
          store.dispatch('on_update_locals', { ...payload, ...res })
        }
        resolve({ ...res, success: true })
      } catch (error) {
        reject({ ...response, error })
      }
    }, 10)
  })
}

export default {
  addItems,
  updateItems,
  getItems,
  getUserDetails,
  deleteItems
}